import { Component } from 'preact';
import { api } from '../../utils/serverClient';
import {
  GOLD, TEXT_BODY, TEXT_MUTED,
  INPUT_STYLE, GOLD_BTN, DANGER_BTN,
} from '../../lib/medievalTheme';
import { Section, AdminButton } from '../AdminPanel';
import { hasCap } from '../../utils/staffAccess';

export default class AdminBroadcastTab extends Component {
  constructor(props) {
    super(props);
    this.state = {
      // Broadcast mail
      subject: '',
      body: '',
      coins: '',
      showBroadcastConfirm: false,
      broadcasting: false,
      broadcastResult: null,
      // Test mail
      testMailType: 'season-reward',
      testMailRecipient: '',
      sendingTestMail: false,
    };
  }

  // ─── Broadcast Mail ──────────────────────────────────
  handleBroadcast = async () => {
    const { subject, body, coins } = this.state;
    this.setState({ broadcasting: true, showBroadcastConfirm: false, broadcastResult: null });
    try {
      const payload = { subject, body };
      const parsedCoins = parseInt(coins, 10);
      if (parsedCoins > 0) payload.coins = parsedCoins;
      const result = await api.post('/admin/broadcast-mail', payload);
      this.setState({
        broadcasting: false,
        broadcastResult: `Sent to ${result.sent ?? '?'} of ${result.total ?? '?'} players`,
      });
      this.props.addLog(`Broadcast mail sent: "${subject}" → ${result.sent ?? '?'} players`);
    } catch (e) {
      this.setState({ broadcasting: false });
      this.props.addLog(`Broadcast failed: ${e.message}`);
    }
  };

  // ─── Test Mail ───────────────────────────────────────
  // Fires one canned mail of the selected type at the recipient (or
  // the calling admin if recipient is blank). Server fills in the
  // type-appropriate fixture body / subject / attachments.
  handleSendTestMail = async () => {
    const { testMailType, testMailRecipient } = this.state;
    this.setState({ sendingTestMail: true });
    try {
      const payload = { type: testMailType };
      const name = testMailRecipient.trim();
      if (name) payload.recipientName = name;
      const result = await api.post('/admin/test-mail', payload);
      this.props.addLog(`Test ${testMailType} mail → ${result.recipientName} (id: ${result.mailId})`);
    } catch (e) {
      this.props.addLog(`Test mail failed: ${e.message}`);
    }
    this.setState({ sendingTestMail: false });
  };

  render() {
    const { subject, body, coins, showBroadcastConfirm, broadcasting, broadcastResult,
      testMailType, testMailRecipient, sendingTestMail } = this.state;
    const { profile } = this.props;

    return (
      <>
        {/* ─── Broadcast Mail ─────────────────────────── */}
        {hasCap(profile, 'mail.broadcast') && (
        <Section title="Broadcast Mail">
          <div className="flex flex-col gap-2">
            <input
              type="text"
              placeholder="Subject"
              value={subject}
              onInput={(e) => this.setState({ subject: e.target.value })}
              className="px-3 py-1.5 text-xs"
              style={INPUT_STYLE}
            />
            <textarea
              placeholder="Body"
              value={body}
              onInput={(e) => this.setState({ body: e.target.value })}
              className="px-3 py-2 text-xs resize-none"
              rows={5}
              style={INPUT_STYLE}
            />
            <div className="flex items-center gap-2">
              <input
                type="number"
                min="0"
                placeholder="Coins (optional)"
                value={coins}
                onInput={(e) => this.setState({ coins: e.target.value })}
                className="w-36 px-2 py-1 text-xs"
                style={INPUT_STYLE}
              />
              {!showBroadcastConfirm ? (
                <AdminButton
                  onClick={() => this.setState({ showBroadcastConfirm: true })}
                  disabled={broadcasting || !subject.trim() || !body.trim()}
                >
                  Send to All Players
                </AdminButton>
              ) : (
                <div className="flex items-center gap-2">
                  <span className="text-[0.6875cqw]" style={{ color: TEXT_BODY }}>
                    Send this mail to all active players?
                  </span>
                  <button
                    type="button"
                    className="px-3 py-1.5 text-[0.6875cqw] font-semibold transition-all hover:scale-[1.03]"
                    style={GOLD_BTN}
                    onClick={this.handleBroadcast}
                    disabled={broadcasting}
                  >
                    {broadcasting ? 'Sending...' : 'Confirm'}
                  </button>
                  <button
                    type="button"
                    className="px-3 py-1.5 text-[0.6875cqw] font-semibold transition-all hover:scale-[1.03]"
                    style={DANGER_BTN}
                    onClick={() => this.setState({ showBroadcastConfirm: false })}
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>
          </div>
          {broadcastResult && (
            <div className="mt-2 text-xs" style={{ color: '#22c55e' }}>
              {broadcastResult}
            </div>
          )}
        </Section>
        )}

        {/* ─── Test Mail ──────────────────────────────── */}
        {hasCap(profile, 'mail.test') && (
        <Section title="Test Mail">
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={testMailType}
              onChange={(e) => this.setState({ testMailType: e.target.value })}
              className="px-2 py-1 text-xs"
              style={INPUT_STYLE}
            >
              <option value="friend">friend</option>
              <option value="news">news</option>
              <option value="auction">auction</option>
              <option value="season-reward">season-reward</option>
              <option value="season-voucher">season-voucher</option>
              <option value="draft-invite">draft-invite</option>
            </select>
            <input
              type="text"
              placeholder="Recipient (blank = self)"
              value={testMailRecipient}
              onInput={(e) => this.setState({ testMailRecipient: e.target.value })}
              className="px-2 py-1 text-xs w-48"
              style={INPUT_STYLE}
            />
            <AdminButton onClick={this.handleSendTestMail} disabled={sendingTestMail}>
              {sendingTestMail ? 'Sending...' : 'Send Test Mail'}
            </AdminButton>
          </div>
          <div className="text-[0.625cqw] mt-1" style={{ color: TEXT_MUTED }}>
            Fires one fixture mail of the chosen type. Defaults to sending to yourself.
            Use to verify the Mailbox UI (Mail tab visibility, voucher picker, etc.) without waiting for a real season rollover or auction settlement.
          </div>
        </Section>
        )}
      </>
    );
  }
}
